import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "throttle-debounce";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Types
import ContactTypes from "app/types/ContactTypes";
// Async
import { getContacts, getContactsAutocomplete } from "app/store/Contacts/Contacts.async";
// Actions
import { ContactsActions } from "app/store/Contacts/Contacts.slice";
// Selectors
import { selectContactsAsOptions, selectParams, selectFilters, selectAutocompleteLoading } from "app/store/Contacts/Contacts.selectors";
// MUI
import { Autocomplete, Box, TextField } from "@mui/material";
// Components
import { Input, Select } from "app/components/Mui";

const ContactsFilter:FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const params = useAppSelector(selectParams);
  const filters = useAppSelector(selectFilters);
  const contactsOptions = useAppSelector(selectContactsAsOptions);
  const autocompleteLoading = useAppSelector(selectAutocompleteLoading);

  const [ search, setSearch ] = useState('');

  // eslint-disable-next-line
  const debounceParams = useCallback(debounce(500, (name:string, value:any) => {
    const updatedParams = { ...params, offset: 0, [name]: value };

    dispatch(getContacts(updatedParams));
    // eslint-disable-next-line
  }), [params]);
  
  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if ( name === 'search' ) setSearch(value);
    debounceParams(name, value);
  }

  const handleChangeReferenceContact = (nextValue:any) => {
    const updatedParams = { ...params, referenceContactId: nextValue ? nextValue.id : null, offset: 0 };
    dispatch(ContactsActions.setFilters({ referenceContact: nextValue || null }));
    dispatch(getContacts(updatedParams));
  }

  const handleClearSearch = () => {
    const updatedParams = { ...params, search: '' };
    setSearch('');
    dispatch(getContacts(updatedParams));
  }
  
  // eslint-disable-next-line
  const debounceContacts = useCallback(debounce(500, (search:string) => {
    dispatch(getContactsAutocomplete({ search, limit: 50, type: ContactTypes.Company }));
  }), []);

  const handleChangeContactInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    debounceContacts(value);
  }

  const typeOptions = useMemo(() => [
    { id: 'all', name: t('labels.all') },
    { id: ContactTypes.Person, name: t('labels.person') },
    { id: ContactTypes.Company, name: t('labels.company') }
  ], [t]);

  useEffect(() => {
    dispatch(getContacts(params));
    dispatch(getContactsAutocomplete({ search: '', limit: 50, type: ContactTypes.Company }));

    return () => {
      dispatch(ContactsActions.resetState());
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 4 }}>
      <Input
        label={t('labels.search')} id="search" name="search" value={search}
        fullWidth size="small" margin="none"
        onChange={handleChange}
        onClear={handleClearSearch}
        sx={{ minWidth: 280 }}
      />
      <Autocomplete
        value={filters.referenceContact as any}
        onChange={(_:any, nextValue:any) => handleChangeReferenceContact(nextValue)}
        options={contactsOptions || []}
        onClose={() => debounceContacts('')}
        sx={{ minWidth: 240 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('labels.company')}
            fullWidth
            margin="none"
            size="small"
            onChange={handleChangeContactInput}
          />
        )}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={autocompleteLoading}
      />
      <Select
        name="type"
        options={typeOptions}
        label={t('labels.type')}
        onChange={handleChange}
        value={params.type || 'all'}
        size="small"
        margin="none"
        sx={{ minWidth: 220 }}
      />
    </Box>
  )
}

export default ContactsFilter;
